import { Envs } from '@ae/envs';
import { Locale } from '../language';
import { beDomains, foreignDomains } from './types';

/**
 * Validates the given URL string.
 *
 * @param url - The URL string to validate.
 * @returns The validated URL string.
 * @throws Will throw an error if the URL is invalid.
 */
const checkValidUrl = (url: string): string => {
  try {
    new URL(url);
    return url;
  } catch (e) {
    throw new Error(`Invalid URL: ${url}`);
  }
};

/**
 * Adjusts the target URL to align with the domain of the request URL if it belongs to a foreign domain.
 *
 * @param requestUrl - The URL of the incoming request.
 * @returns The adjusted URL string.
 * @throws Will throw an error if the target URL is invalid.
 */
const alignRequestUrlToHttps = (url: string): string => {
  const urlObj = new URL(url);
  urlObj.protocol = 'https';
  return urlObj.toString();
};

/**
 * Adjusts the target URL to align with the domain of the request URL if it belongs to a foreign domain.
 *
 * @param requestUrl - The URL of the incoming request.
 * @param targetUrl - The target URL to adjust.
 * @returns The adjusted URL string.
 * @throws Will throw an error if the target URL is invalid.
 */
const alignUrlToLocale = (requestUrl: string, targetUrl: string): string => {
  const matchedDomain = Object.entries(beDomains).find(([, domain]) =>
    requestUrl.includes(domain)
  );

  if (!matchedDomain) {
    return targetUrl;
  }

  const [locale] = matchedDomain;
  const domainUrl = new URL(beDomains[locale as Locale]);
  const urlObj = new URL(targetUrl);
  urlObj.hostname = domainUrl.hostname;
  urlObj.protocol = domainUrl.protocol;

  return urlObj.toString();
};

/**
 * Adjusts the target URL to align with the domain of the request URL if it belongs to a foreign domain.
 *
 * @param requestUrl - The URL of the incoming request.
 * @param targetUrl - The target URL to adjust.
 * @returns The adjusted URL string.
 * @throws Will throw an error if the target URL is invalid.
 */
const alignUrlToForeignDomain = (
  requestUrl: string | undefined,
  targetUrl: string
): string => {
  if (!requestUrl) return targetUrl;

  const matchedDomain = Object.entries(foreignDomains).find(([, domain]) =>
    requestUrl.includes(domain)
  );

  if (!matchedDomain) {
    return targetUrl;
  }

  const [locale] = matchedDomain;
  const domainUrl = new URL(foreignDomains[locale as Locale]);
  const urlObj = new URL(targetUrl);
  urlObj.hostname = domainUrl.hostname;
  urlObj.protocol = domainUrl.protocol;

  return urlObj.toString();
};

/**
 * Constructs and validates the assets path.
 *
 * @returns The validated assets path.
 */
export const getAssetsPath = (): string => {
  return Envs.basePath ?? '';
};

/**
 * Constructs and validates the API path based on the request URL and base API path.
 *
 * @param requestUrl - The URL of the incoming request.
 * @returns The validated API path.
 */
export const getApiEndpoint = (
  requestUrl: string,
  apiEndpoint: string
): string => {
  return checkValidUrl(
    alignUrlToLocale(
      requestUrl,
      alignUrlToForeignDomain(alignRequestUrlToHttps(requestUrl), apiEndpoint)
    )
  );
};

/**
 * Retrieves the backend domain for a given locale.
 *
 * @param locale - The locale for which to retrieve the backend domain.
 * @returns The backend domain associated with the locale.
 */
export const getBeDomain = (locale: Locale): string => {
  return beDomains[locale];
};

/**
 * Retrieves the foreign domain for a given locale.
 *
 * @param locale - The locale for which to retrieve the foreign domain.
 * @returns The foreign domain associated with the locale.
 */
export const getForeignDomain = (locale: Locale): string => {
  return foreignDomains[locale];
};

/**
 * Extracts the racine domain from a given URL.
 *
 * @param url - The URL to extract the racine domain from.
 * @returns The extracted racine domain or an empty string if the URL is invalid.
 */
export const extractHostnameRacine = (url: string) => {
  try {
    const urlObj = new URL(url);
    urlObj.port = '';
    return `.${urlObj.hostname.substring(urlObj.hostname.indexOf('ardennes-etape'))}`;
  } catch {
    return '';
  }
};
